import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Phone, MessageCircle, ArrowRight, DollarSign, Clock, Baby, Dog, Car, Users, MapPin, RotateCcw, Timer, ShoppingBag, Stethoscope, GraduationCap, Ship, PartyPopper, Moon } from 'lucide-react';
import PageSEO from '@/components/PageSEO';
import { BUSINESS } from '@/lib/constants';
import { webPage, service, breadcrumb } from '@/lib/schemas';

const FEATURES = [
  { icon: DollarSign, title: 'Starting at $12', desc: 'Transparent pricing confirmed before your ride — no surge, no surprises.' },
  { icon: MapPin, title: 'Multiple stops', desc: 'Run errands, make pickups — we accommodate multi-stop trips.' },
  { icon: Dog, title: 'Pet-friendly', desc: 'Your pet is welcome to ride along.' },
  { icon: Car, title: 'Sedan — up to 3 riders', desc: 'Comfortable sedan for solo trips or small groups.' },
  { icon: Users, title: 'Minivan — up to 6 riders', desc: 'Spacious minivan for families or larger groups.' },
  { icon: RotateCcw, title: 'Round trips', desc: 'Book outbound and return in one request.' },
  { icon: Timer, title: 'Waiting time', desc: 'Driver waits while you handle your stop — small fee applies.' },
  { icon: Baby, title: 'Child seats', desc: 'Pre-installed upon request — rear-facing, forward-facing, or booster.' },
];

const USE_CASES = [
  { icon: ShoppingBag, label: 'Errands & shopping' },
  { icon: Stethoscope, label: 'Medical appointments' },
  { icon: GraduationCap, label: 'School pickup & drop-off' },
  { icon: Ship, label: 'Ferry connections' },
  { icon: PartyPopper, label: 'Family outings & events' },
  { icon: Moon, label: 'Late night rides home' },
];

const FAQS = [
  { q: 'How much does a local ride cost?', a: 'Local rides start at $12. Your fare is confirmed before the ride — no surge pricing, no surprises. Final cost depends on distance, stops, and time of day.' },
  { q: 'Can I make multiple stops?', a: 'Yes. Let us know your stops when you book and we\'ll accommodate them. Each additional stop may adjust the fare slightly.' },
  { q: 'Do you allow pets in the car?', a: 'Absolutely. Pets are welcome on all rides. Just let us know when booking so your driver is prepared.' },
  { q: 'Do you have child car seats?', a: 'Yes — we offer rear-facing, forward-facing, and booster seats, pre-installed upon request. Include your child\'s age and weight when you book.' },
  { q: 'What vehicles are available?', a: 'We operate sedans (up to 3 passengers) and minivans (up to 6 passengers). Let us know your group size and we\'ll match the right vehicle.' },
];

const HERO_IMG = 'https://images.unsplash.com/photo-1764096535135-936651fee5df?fm=jpg&q=85&w=1400&auto=format&fit=crop';

const LOCAL_SCHEMAS = [
  webPage('Local Rides in Staten Island', 'Local cab service for errands, appointments, shopping, ferry connections, and family rides in Staten Island.', '/local-rides-staten-island'),
  service('Local Rides in Staten Island', 'Local cab service', 'Local cab service for errands, appointments, shopping, ferry connections, family rides, and everyday transportation in Staten Island.', '/local-rides-staten-island', { '@type': 'Offer', priceCurrency: 'USD', price: '12', description: 'Local rides starting as low as $12. Final fare depends on pickup, destination, timing, and ride details.' }),
  breadcrumb('Local Rides', '/local-rides-staten-island'),
];

export default function LocalRides() {
  return (
    <>
      <PageSEO
        title="Local Rides in Staten Island — Taxi & Car Service | Island Ride"
        description="Need a local ride in Staten Island? Errands, appointments, ferry connections, and family rides. Starting at $12. Pre-installed child seats upon request."
        path="/local-rides-staten-island"
        schemas={LOCAL_SCHEMAS}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Tree-lined residential street in Staten Island — Island Ride local cab service for everyday errands, appointments, and family transportation"
          title="Staten Island neighborhood street"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/60 to-background/20" />
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary" data-testid="local-rides-h1">
            Local Rides in Staten Island
          </h1>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-lg leading-relaxed">
            Everyday transportation across Staten Island — errands, appointments, ferry connections, and more.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm font-medium text-primary">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/90 px-3 py-1">
              <DollarSign className="h-3.5 w-3.5" /> Starting at $12
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/90 px-3 py-1">
              <Car className="h-3.5 w-3.5" /> Sedans · Minivans · SUVs
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/90 px-3 py-1">
              No surge pricing
            </span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild data-testid="local-rides-cta-request">
              <Link to="/request-a-ride">Request a ride<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline" className="bg-background/80" data-testid="local-rides-cta-call">
              <a href={BUSINESS.phoneTel}><Phone className="h-4 w-4" /><span className="ml-1.5">{BUSINESS.phone}</span></a>
            </Button>
          </div>
        </div>
      </section>

      {/* What we offer */}
      <section className="section-pad">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary" data-testid="local-rides-features-heading">
            What's included
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => (
              <Card
                key={f.title}
                className="p-5 transition-[box-shadow,transform] duration-200 hover:-translate-y-1 hover:shadow-lg"
                style={{ background: 'linear-gradient(to top, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
                data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary">
                  <f.icon className="h-4.5 w-4.5" />
                </div>
                <h3 className="mt-3 font-display text-sm font-semibold">{f.title}</h3>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What locals use us for */}
      <section
        className="section-pad"
        style={{ background: 'linear-gradient(to bottom, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary" data-testid="local-rides-use-cases-heading">
            What locals use us for
          </h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {USE_CASES.map((u) => (
              <div
                key={u.label}
                className="flex flex-col items-center text-center rounded-xl bg-background border p-4 transition-transform duration-150 hover:-translate-y-0.5"
                data-testid={`use-case-${u.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary">
                  <u.icon className="h-5 w-5" />
                </div>
                <p className="mt-2.5 font-display text-xs font-semibold leading-tight">{u.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage + CTA */}
      <section className="bg-secondary border-y">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary" data-testid="local-rides-coverage-heading">
                All of Staten Island
              </h2>
              <p className="mt-2 text-muted-foreground">
                From St. George to Tottenville — we cover every neighborhood.{' '}
                <Link to="/staten-island-service-area" className="text-primary font-medium hover:underline">See our service area</Link>.{' '}
                Need an airport ride? <Link to="/airport-rides-staten-island" className="text-primary font-medium hover:underline">See airport rides</Link>.{' '}
                Have questions? <Link to="/contact" className="text-primary font-medium hover:underline">Contact us</Link>.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button asChild data-testid="coverage-cta-request">
                <Link to="/request-a-ride">Request a ride<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
              </Button>
              <Button asChild variant="outline" data-testid="coverage-cta-whatsapp">
                <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /><span className="ml-1.5">WhatsApp</span></a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" style={{ background: 'linear-gradient(to right, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}>
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary" data-testid="local-rides-faq-heading">
            Frequently asked questions
          </h2>
          <div className="mt-8 max-w-3xl">
            <Accordion type="single" collapsible className="space-y-2" data-testid="local-rides-faq">
              {FAQS.map((faq) => (
                <AccordionItem key={faq.q} value={faq.q} className="rounded-lg border bg-background px-5">
                  <AccordionTrigger className="text-sm font-semibold text-left py-4 hover:no-underline" data-testid={`faq-trigger-${faq.q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </>
  );
}
